import * as React from 'react'
import { Pair } from 'widgets-for-react'
import { List } from 'immutable'
import { CookieAccepted, CookiebarStyle, CookieName, CookiePlaceholderStyle, CookieState } from './cookiebar.types'
import i18next from 'i18next'
import { FormControls } from '../../../Shared/Frontend/forms'

export function getClassName<style>(options: { style?: style }, className: keyof style, notset: string = ''): string {
  return String(options && options.style && options.style[className] ? options.style[className] : notset)
}

export const cookiebarClasses: CookiebarStyle = {
  cookiebarClassName: 'cookiebar',
  cookiebarOuterClassName: 'cookiebar__outer ls-container--m ls-gutter',
  cookiebarInnerClassName: 'cookiebar__inner',
  cookiebarTextClassName: 'cookiebar__text',
  cookiebarTitleClassName: 'cookiebar__title title--xs',
  cookiebarIntroClassName: 'cookiebar__intro text--styled text--s',
  cookiebarFooterClassName: 'cookiebar__footer',
  cookiebarClickableWrapperClassName: 'cookiebar__action',
  cookiebarClickablePrimaryClassName: 'cookiebar__accept button button--primary',
  cookiebarClickableSecondaryClassName: 'cookiebar__decline button button--tertiary',
  cookiebarClickableLabelClassName: 'button__label text--s',
  cookiebarClickableIconClassName: undefined,
  cookiebarSettingsClassName: 'cookiebar__settings link link--default',
  cookiebarSettingsLabelClassName: 'link__label text--s',
  cookiebarSettingsIconClassName: 'link__icon',
  cookiebarFormClassName: 'cookiebar__form',
  cookiebarFormListClassName: 'cookiebar__list',
  cookiebarFormItemClassName: 'cookiebar__item',
  cookiebarFormFooterClassName: 'cookiebar__formaction',
  cookiebarSubmitClassName: 'cookiebar__submit button button--primary',
  cookiebarSubmitLabelClassName: 'button__label text--s',
  cookiebarSubmitIconClassName: undefined,
  settingsIconId: undefined,
  submitIconId: undefined,
  iconId: undefined,
}

export const cookiePlaceholderClasses: CookiePlaceholderStyle = {
  cookiesClassName: 'cookies',
  cookiesInnerClassName: 'cookies__inner',
  cookiesTextClassName: 'cookies__text text--styled text--s',
  cookiesClickableClassName: 'cookies__clickable button button--primary',
  cookiesClickableLabelClassName: 'button__label text--s',
  cookiesClickableIconClassName: undefined,
  iconId: undefined,
}

export const CookiebarLayout = function () {
  let CookiePlaceholderLayout = (props: {
    text: string | JSX.Element
    acceptLabel: string
    onAccept: () => void
    options: { style?: CookiePlaceholderStyle; key?: string }
    SVGIcon?: {
      width?: string
      height?: string
    }
  }) => {
    const { options, text, acceptLabel, onAccept, SVGIcon } = props
    return (
      <div className={getClassName(options, 'cookiesClassName', 'bg-light ratio ratio-16x9')}>
        <div
          className={getClassName(
            options,
            'cookiesInnerClassName',
            'd-flex flex-column align-items-center justify-content-center px-gutter text-center'
          )}
        >
          <div className={getClassName(options, 'cookiesTextClassName', 'mb-3 mw-100')}>{text}</div>
          <button
            className={getClassName(options, 'cookiesClickableClassName', 'btn btn-primary btn-sm')}
            onClick={onAccept}
          >
            <span className={getClassName(options, 'cookiesClickableLabelClassName', '')}>{acceptLabel}</span>
            {SVGIcon && (
              <svg
                className={getClassName(options, 'cookiesClickableIconClassName', 'd-none')}
                aria-hidden='true'
                width={SVGIcon.width ? SVGIcon.width : '24px'}
                height={SVGIcon.height ? SVGIcon.height : '24px'}
              >
                <use xlinkHref={getClassName(options, 'iconId', '')} />
              </svg>
            )}
          </button>
        </div>
      </div>
    )
  }

  let CookiebarLayout = (props: {
    title: string
    intro: string
    introSettings: string
    showOptionsLabel: string
    showOptions: boolean
    toggleOptions: () => void
    acceptAllLabel: string
    onAcceptAll: () => void
    declineAllLabel: string
    onDeclineAll: () => void
    applySettingsLabel: string
    onApplySettings: () => void
    onSetSingle: (cookieName: CookieName, state: CookieAccepted) => void
    state: List<Pair<CookieName, CookieState>>
    options: { style?: CookiebarStyle; key?: string }
    SVGIcon?: {
      width?: string
      height?: string
    }
    ExpandSVGIcon?: {
      width?: string
      height?: string
    }
    SubmitSVGIcon?: {
      width?: string
      height?: string
    }
  }) => {
    const {
      options,
      title,
      intro,
      introSettings,
      showOptions,
      showOptionsLabel,
      toggleOptions,
      acceptAllLabel,
      onAcceptAll,
      declineAllLabel,
      onDeclineAll,
      applySettingsLabel,
      onApplySettings,
      state,
      onSetSingle,
      SVGIcon,
      ExpandSVGIcon,
      SubmitSVGIcon
    } = props
    return (
      <section
        className={getClassName(options, 'cookiebarClassName', 'position-fixed bottom-0 bg-white shadow py-4 w-100')}
        role='alert'
        id="cookiebar"
        aria-labelledby='a11y-cookiebar-title'
      >
        <div className={getClassName(options, 'cookiebarOuterClassName', 'mw-container-m px-gutter mx-auto')}>
          <div className={getClassName(options, 'cookiebarInnerClassName', '')}>
            <div className={getClassName(options, 'cookiebarTextClassName', '')}>
              <h2 id='a11y-cookiebar-title' className={getClassName(options, 'cookiebarTitleClassName', 'h3')}>
                {title}
              </h2>
              <div
                className={getClassName(options, 'cookiebarIntroClassName', 'small')}
                dangerouslySetInnerHTML={{ __html: showOptions === false ? intro : introSettings }}
              />
            </div>
            {showOptions === false ? (
              <div className={getClassName(options, 'cookiebarFooterClassName', 'mt-3')}>
                <div className={getClassName(options, 'cookiebarClickableWrapperClassName', 'd-block d-sm-inline-block')}>
                  <button
                    className={getClassName(options, 'cookiebarClickablePrimaryClassName', 'btn btn-primary btn-sm')}
                    onClick={onAcceptAll}
                  >
                    <span className={getClassName(options, 'cookiebarClickableLabelClassName', '')}>
                      {acceptAllLabel}
                    </span>
                    {SVGIcon && (
                      <svg
                        className={getClassName(options, 'cookiebarClickableIconClassName', 'd-none')}
                        aria-hidden='true'
                        width={SVGIcon.width ? SVGIcon.width : '24px'}
                        height={SVGIcon.height ? SVGIcon.height : '24px'}
                      >
                        <use xlinkHref={getClassName(options, 'iconId', '#icon--chevron-down')} />
                      </svg>
                    )}
                  </button>
                </div>
                <div
                  className={getClassName(
                    options,
                    'cookiebarClickableWrapperClassName',
                    'd-block d-sm-inline-block ms-sm-3 mt-3 mt-sm-0'
                  )}
                >
                  <button
                    className={getClassName(
                      options,
                      'cookiebarClickableSecondaryClassName',
                      'btn btn-outline-primary btn-sm'
                    )}
                    onClick={onDeclineAll}
                  >
                    <span className={getClassName(options, 'cookiebarClickableLabelClassName', '')}>
                      {declineAllLabel}
                    </span>
                    {SVGIcon && (
                      <svg
                        className={getClassName(options, 'cookiebarClickableIconClassName', 'd-none')}
                        aria-hidden='true'
                        width={SVGIcon.width ? SVGIcon.width : '24px'}
                        height={SVGIcon.height ? SVGIcon.height : '24px'}
                      >
                        <use xlinkHref={getClassName(options, 'iconId', '#icon--chevron-right-large')} />
                      </svg>
                    )}
                  </button>
                </div>

                {/* Expand button */}
                <div
                  className={getClassName(
                    options,
                    'cookiebarClickableWrapperClassName',
                    'd-block d-sm-inline-block ms-sm-3 mt-3 mt-sm-0'
                  )}
                >
                  <button
                    className={getClassName(
                      options,
                      'cookiebarSettingsClassName',
                      'btn btn-secondary btn-sm'
                    )}
                    onClick={toggleOptions}
                    aria-controls='a11y-cookiebar-toggle'
                    aria-expanded={showOptions}
                  >
                    <span className={getClassName(options, 'cookiebarSettingsLabelClassName', '')}>{showOptionsLabel}</span>
                    {/* {ExpandSVGIcon && (
                      <svg
                        className={getClassName(options, 'cookiebarSettingsIconClassName', 'd-none')}
                        aria-hidden='true'
                        width={ExpandSVGIcon.width ? ExpandSVGIcon.width : '24px'}
                        height={ExpandSVGIcon.height ? ExpandSVGIcon.height : '24px'}
                      >
                        <use xlinkHref={getClassName(options, 'settingsIconId', '#icon--chevron-down')} />
                      </svg>
                    )} */}
                  </button>
                </div>
              </div>
            ) : <></>}

            <div
              id='a11y-cookiebar-toggle'
              className={getClassName(options, 'cookiebarFormClassName', 'mt-3')}
              aria-hidden={!showOptions}
            >
              {showOptions === true ? (
                <>
                  <ul
                    className={getClassName(
                      options,
                      'cookiebarFormListClassName',
                      'd-block mt-3 border-top pt-3 small'
                    )}
                  >
                    {state.map((c) => {
                      const { isAccepted, htmlFor, isDisabled, label } = c[1]
                      return (
                        <li className={getClassName(options, 'cookiebarFormItemClassName', '')} key={`cookie-${label}`}>
                          <FormControls.FormCheckInGroup
                            name='cookies'
                            required={false}
                            disabled={c[1].isRequired}
                            items={[
                              {
                                controlId: htmlFor,
                                label: label
                                  .split(' ')
                                  .map((l: string) => i18next.t(l))
                                  .join('. '),
                                checked: c[1].isRequired || isAccepted === 'yes',
                                onChange: (v) => onSetSingle(c[0], v ? 'yes' : 'no')
                              }
                            ]}
                          />
                        </li>
                      )
                    })}
                  </ul>

                  <div
                    className={getClassName(
                      options,
                      'cookiebarFormFooterClassName',
                      'd-block d-sm-inline-block'
                    )}
                  >
                    <button
                      className={getClassName(options, 'cookiebarSubmitClassName', 'btn btn-primary btn-sm')}
                      onClick={onApplySettings}
                    >
                      <span className={getClassName(options, 'cookiebarSubmitLabelClassName', '')}>
                        {applySettingsLabel}
                      </span>
                      {/* {SubmitSVGIcon && (
                        <svg
                          className={getClassName(options, 'cookiebarSubmitIconClassName', 'd-none')}
                          aria-hidden='true'
                          width={SubmitSVGIcon.width ? SubmitSVGIcon.width : '24px'}
                          height={SubmitSVGIcon.height ? SubmitSVGIcon.height : '24px'}
                        >
                          <use xlinkHref={getClassName(options, 'submitIconId', '#icon--chevron-right-large')} />
                        </svg>
                      )} */}
                    </button>
                  </div>
                </>
              ) : <></>}
            </div>
          </div>
        </div>
      </section>
    )
  }
  return {
    CookiebarLayout,
    CookiePlaceholderLayout
  }
}

export const cookiePlaceholderText = (type: 'iframe' | 'media'): JSX.Element => {
  switch (type) {
    case 'iframe':
      return <p>To be able to view this content you need to accept the cookies that come from this third party.</p>
    case 'media':
      return <p>To be able to view this content you need to accept the cookies that come from this third party.</p>
    default:
      return <p>To be able to view this content you need to accept the cookies that come from this third party.</p>
  }
}

export const cookiePlaceholderClickable = (type: 'iframe' | 'media'): string => {
  switch (type) {
    case 'iframe':
      return 'Accept cookies'
    case 'media':
      return 'Accept cookies'
    default:
      return 'Accept cookies'
  }
}
